/**
 * Money crosses the wire as a decimal string (`"-118.44"`). It is parsed once,
 * here, into integer cents, and nothing downstream does float arithmetic on it
 * — Cashflow, Budget and Reports all sum long lists, and binary floats drift
 * across those sums in ways that show up as an off-by-a-cent total nobody can
 * reproduce.
 *
 * `Money` is the only component that renders currency; `formatCents` is the
 * plain-string form used for tests, exports and comparisons.
 */

// Anchored, and deliberately narrow: an optional leading minus, at least one
// digit, and at most two decimal places. Everything else — thousands
// separators, currency symbols, exponents, three decimals — is a display
// string or a precision decision this parser has no business guessing at.
const MONEY = /^-?\d+(\.\d{1,2})?$/

export function parseMoney(value: string): number {
  if (typeof value !== 'string') {
    throw new Error(`parseMoney: not a money string: ${JSON.stringify(value)}`)
  }
  const trimmed = value.trim()
  if (!MONEY.test(trimmed)) {
    throw new Error(`parseMoney: not a money string: ${JSON.stringify(value)}`)
  }

  const negative = trimmed.startsWith('-')
  const [whole = '0', frac = ''] = trimmed.replace('-', '').split('.')
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'))

  // Past 2^53 cents the arithmetic above stops being exact and the result
  // round-trips to a different string — "integer cents" would be claiming a
  // precision it no longer has. The threshold is around $90 trillion, so this
  // cannot fire on real money; it fires on a corrupt payload, which is the
  // case worth naming rather than silently rounding.
  if (!Number.isSafeInteger(cents)) {
    throw new Error(`parseMoney: value exceeds exact cent precision: ${JSON.stringify(value)}`)
  }

  // `cents !== 0` guard: "-0.00" would otherwise yield -0, which is not
  // Object.is-equal to 0, so a total that cancels out compares unequal to zero
  // and Math.sign reports -1 on a balance of nothing.
  return negative && cents !== 0 ? -cents : cents
}

/**
 * What a person TYPES into an amount field, as cents — through `parseMoney`
 * and nothing else.
 *
 * Two pieces of input normalisation come first, and only two: one leading `$`
 * (with any space after it), and the commas of a thousands grouping
 * (`1,200.10`). A comma that is not followed by exactly three digits is left
 * in place, so `1,5` is refused rather than read as fifteen. Everything else is
 * `parseMoney`'s call: `12abc` throws, never truncates to its leading number,
 * which is what a float parse would do.
 *
 * Throws on blank input too — whether blank means "required" is the form's
 * decision, made before it calls this.
 */
export function parseMoneyInput(text: string): number {
  const normalised = text.trim().replace(/^\$\s*/, '').replace(/,(?=\d{3}(?:\D|$))/g, '')
  return parseMoney(normalised)
}

export function formatCents(cents: number): string {
  if (!Number.isSafeInteger(cents)) {
    throw new Error(`formatCents: expected safe integer cents, got ${JSON.stringify(cents)}`)
  }
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

/**
 * `GET /api/v1/transactions`'s per-row `amount`, negated for display.
 *
 * **Storage is expense-positive.** The backend's "What the API actually is"
 * traces the chain: `plaid_mapper.py:47` negates Plaid's outflow-positive
 * figure into an inflow-positive DTO (`providers/normalized.py:17`), and
 * `_to_storage_amount()` (`providers/persistence.py:38`) negates it AGAIN on
 * the way into the table. So `parseMoney(t.amount)` is a correct PARSE and a
 * wrong DISPLAY VALUE: a $118.44 grocery bill arrives as `"118.44"`
 * (positive) and a paycheque as `"-4280.00"` (negative) — the opposite of
 * what `kind="flow"` needs to colour and sign correctly (`--pos` green for a
 * real gain). Verified on the wire against the seeded backend (team-lead,
 * design gap-fill pass, 2026-09-17), not inferred from the mapper chain
 * alone — the exact "stopped at the wrong flip" trap this epic's own
 * retracted `/transactions/summary` review already fell into once.
 *
 * **Lives here, beside `parseMoney`, rather than in a feature module.** It
 * shipped twice as a feature-module-local mapping bug — first fixed in
 * `useAccountTransactions.ts` (#438), then found wrong again in
 * `useMerchants.ts` (#439) because the accounts module wasn't the obvious
 * place for a THIRD module to look for a wire-format rule. The convention
 * belongs to the endpoint, not to whichever screen happened to read it
 * first — the same reasoning that puts `parseMoney` itself at the API
 * boundary rather than in `Money.tsx`.
 *
 * **`/transactions/summary`'s own `net` is NOT run through this.** `net` is
 * ALREADY `total_income − total_expenses` on that same expense-positive
 * convention — a computed AGGREGATE, not an instance of the per-row field
 * this function corrects. Negating it a second time would reintroduce
 * exactly the inversion #425 D1's own retraction closed. `useAccountSummary
 * .ts` reads `net` untouched; this function exists for a raw, per-row
 * `amount` only — `useAccountTransactions.ts` and `useMerchants.ts`, today.
 */
export function signedTransactionCents(rawCents: number): number {
  // `-0` is not `Object.is`-equal to 0, and a zero-amount row would otherwise
  // carry it into every sum and `Math.sign` downstream (see `parseMoney`).
  return rawCents === 0 ? 0 : -rawCents
}
